/**
 * API Client for Quality Project Management
 * Standalone version - no Hub dependency
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Types
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface User {
  id: number;
  username: string;
  name: string;
  email: string;
  role: string;
  department_id: number | null;
  services: string[];
  service_roles: Record<string, string>;
  is_active: boolean;
  employee_id: number | null;
}

export interface LoginResponse {
  token_type: string;
  expires_in: number;
  user: User;
}

export interface Department {
  id: number;
  name: string;
  description: string | null;
  manager_user_id: number | null;
  user_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  username: string;
  name: string;
  email: string;
  password: string;
  role?: string;
  department_id?: number;
  services?: string[];
  service_roles?: Record<string, string>;
  is_active?: boolean;
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  role?: string;
  department_id?: number;
  services?: string[];
  service_roles?: Record<string, string>;
  is_active?: boolean;
}

export interface CreateDepartmentRequest {
  name: string;
  description?: string;
  manager_user_id?: number;
}

export interface UpdateDepartmentRequest {
  name?: string;
  description?: string;
  manager_user_id?: number;
}

class ProjectManagementAPI {
  private baseUrl: string;
  private isLoggedIn: boolean = false;

  constructor() {
    this.baseUrl = API_BASE_URL;
    // SECURITY: Check if user data exists (indicates previous session)
    this.isLoggedIn = !!localStorage.getItem('globalUser');
  }

  private clearSession(): void {
    this.isLoggedIn = false;
    localStorage.removeItem('globalUser');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {})
    };

    // SECURITY: Include credentials to send/receive HTTP-only cookies
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include'
    });

    if (response.status === 401) {
      // Try to refresh token (cookie-based)
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
          ...options,
          headers,
          credentials: 'include'
        });
        if (!retryResponse.ok) {
          throw new Error(`Request failed: ${retryResponse.status}`);
        }
        return retryResponse.json();
      }
      this.clearSession();
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || error.detail || 'Request failed');
    }

    return response.json();
  }

  private async tryRefreshToken(): Promise<boolean> {
    try {
      // SECURITY: Refresh token is in HTTP-only cookie
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      return response.ok;
    } catch {
      return false;
    }
  }

  // Auth methods
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    // SECURITY: Tokens are set as HTTP-only cookies by the server
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });

    this.isLoggedIn = true;
    localStorage.setItem('globalUser', JSON.stringify(response.user));

    return response;
  }

  async logout(): Promise<void> {
    try {
      // SECURITY: Server will clear HTTP-only cookies
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.clearSession();
    }
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('/auth/me');
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      })
    });
  }

  async updateProfile(data: { name?: string; email?: string }): Promise<User> {
    return this.request<User>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  // User methods
  async getAllUsers(params?: { role?: string; is_active?: boolean; department_id?: number }): Promise<User[]> {
    const searchParams = new URLSearchParams();
    if (params?.role) searchParams.set('role', params.role);
    if (params?.is_active !== undefined) searchParams.set('is_active', String(params.is_active));
    if (params?.department_id) searchParams.set('department_id', String(params.department_id));

    const query = searchParams.toString();
    return this.request<User[]>(`/users${query ? `?${query}` : ''}`);
  }

  async getUser(userId: number): Promise<User> {
    return this.request<User>(`/users/${userId}`);
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    return this.request<User>('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateUser(userId: number, userData: UpdateUserRequest): Promise<User> {
    return this.request<User>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async updateUserPassword(userId: number, password: string): Promise<void> {
    await this.request(`/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password })
    });
  }

  async deleteUser(userId: number): Promise<void> {
    await this.request(`/users/${userId}`, { method: 'DELETE' });
  }

  async activateUser(userId: number): Promise<void> {
    await this.request(`/users/${userId}/activate`, { method: 'POST' });
  }

  // Department methods
  async getDepartments(): Promise<Department[]> {
    return this.request<Department[]>('/departments');
  }

  async getDepartment(deptId: number): Promise<Department> {
    return this.request<Department>(`/departments/${deptId}`);
  }

  async createDepartment(data: CreateDepartmentRequest): Promise<Department> {
    return this.request<Department>('/departments', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateDepartment(deptId: number, data: UpdateDepartmentRequest): Promise<Department> {
    return this.request<Department>(`/departments/${deptId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteDepartment(deptId: number): Promise<void> {
    await this.request(`/departments/${deptId}`, { method: 'DELETE' });
  }

  // Utility methods
  isAuthenticated(): boolean {
    // SECURITY: We track login state since tokens are in HTTP-only cookies (not accessible via JS)
    return this.isLoggedIn;
  }

  getStoredUser(): User | null {
    const stored = localStorage.getItem('globalUser');
    return stored ? JSON.parse(stored) : null;
  }

  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl.replace('/api', '')}/health`);
      return response.ok;
    } catch {
      return false;
    }
  }
}

export const projectManagementAPI = new ProjectManagementAPI();
export default projectManagementAPI;
